/* global axios */
import appConfig from '../../../shared/helpers/appConfig';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// Matches the mock WhatsApp inbox (id 999999) added in getInboxes/getInbox
// (store/modules/inboxes.js) for VUE_APP_ENV=development.
const MOCK_WHATSAPP_INBOX_ID = 999999;

const buildMockTemplates = () => [
  {
    id: 'mock-template-001',
    name: 'welcome_promo_en',
    language: 'en',
    category: 'MARKETING',
    status: 'APPROVED',
    created_at: '2026-06-01T10:00:00Z',
    updated_at: '2026-06-02T09:00:00Z',
    components: [
      {
        type: 'HEADER',
        format: 'TEXT',
        text: 'Welcome to {{1}}!',
        example: { header_text: ['Acme Store'] },
      },
      {
        type: 'BODY',
        text: 'Hi {{1}}, thanks for joining! Enjoy {{2}} off your first order.',
        example: { body_text: [['John', '20%']] },
      },
      { type: 'FOOTER', text: 'Reply STOP to unsubscribe' },
      {
        type: 'BUTTONS',
        buttons: [
          { type: 'QUICK_REPLY', text: 'Shop Now' },
          { type: 'URL', text: 'Visit Website', url: 'https://example.com' },
        ],
      },
    ],
  },
  {
    id: 'mock-template-002',
    name: 'order_shipped_media',
    language: 'en_US',
    category: 'UTILITY',
    status: 'APPROVED',
    created_at: '2026-06-05T12:00:00Z',
    updated_at: '2026-06-05T12:00:00Z',
    components: [
      {
        type: 'HEADER',
        format: 'IMAGE',
        example: {
          header_handle: [
            'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80',
          ],
        },
      },
      {
        type: 'BODY',
        text: 'Your order #{{1}} has shipped and will arrive by {{2}}.',
        example: { body_text: [['48213', 'Friday']] },
      },
    ],
  },
  {
    id: 'mock-template-003',
    name: 'otp_verification',
    language: 'en',
    category: 'AUTHENTICATION',
    status: 'PENDING',
    created_at: '2026-07-10T08:00:00Z',
    updated_at: '2026-07-10T08:00:00Z',
    components: [
      {
        type: 'BODY',
        text: '{{1}} is your verification code. For your security, do not share this code.',
      },
      { type: 'FOOTER', text: 'This code expires in 10 minutes.' },
      {
        type: 'BUTTONS',
        buttons: [{ type: 'OTP', text: 'Copy Code', otp_type: 'copy_code' }],
      },
    ],
  },
  {
    id: 'mock-template-004',
    name: 'flash_sale_banner',
    language: 'en',
    category: 'MARKETING',
    status: 'REJECTED',
    rejected_reason:
      'The template content was flagged for promotional language that violates WhatsApp Business Policy 2.9 (Prohibited Content).',
    created_at: '2026-06-20T15:30:00Z',
    updated_at: '2026-06-21T08:00:00Z',
    components: [
      {
        type: 'BODY',
        text: 'FLASH SALE! Everything must go! {{1}} OFF today only!',
        example: { body_text: [['70%']] },
      },
    ],
  },
  // The template the mock campaigns use (store/modules/whatsappCampaignsMock.js), so
  // editing a mock campaign preselects it and its preview matches the campaign.
  {
    id: 'mock-template-005',
    name: 'kumho_mock_clearance_en',
    language: 'en',
    category: 'MARKETING',
    status: 'APPROVED',
    created_at: '2026-06-25T09:00:00Z',
    updated_at: '2026-06-25T09:00:00Z',
    components: [
      {
        type: 'HEADER',
        format: 'IMAGE',
        example: {
          header_handle: [
            'https://images.unsplash.com/photo-1557821552-17105176677c?w=800&q=80',
          ],
        },
      },
      {
        type: 'BODY',
        text: 'Hi {{1}}! 🎉 Special offer just for you: {{2}} on all items. Limited time only!',
        example: { body_text: [['John Doe', '50% OFF']] },
      },
      { type: 'FOOTER', text: 'Reply STOP to unsubscribe' },
      {
        type: 'BUTTONS',
        buttons: [
          { type: 'QUICK_REPLY', text: 'View Offers' },
          { type: 'URL', text: 'Shop Now', url: 'https://example.com/shop' },
        ],
      },
    ],
  },
];

// Mutable so a mock delete is reflected on the next mock fetch.
let mockTemplates = buildMockTemplates();

function isValidUrlTemplate(url) {
  if (!url || typeof url !== 'string') return false;
  if (url.includes('{{1}}')) {
    const urlObj = (() => { try { return new URL(url.replace('{{1}}', 'placeholder')); } catch { return null; } })();
    if (!urlObj) return false;
    const pathValid = /\/\{\{1\}\}/.test(url) || /\?.*=\{\{1\}\}/.test(url);
    if (!pathValid) return false;
    return (urlObj.protocol === 'https:' || urlObj.protocol === 'http:') && !!urlObj.host;
  } else {
    try {
      const parsed = new URL(url);
      return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && !!parsed.host;
    } catch (e) {
      return false;
    }
  }
}

export const state={
  currentCategory: 'MARKETING',
  selectedFormat: "POSITIONAL",
  selectedMessageType: "Default",
  templateName: "",
  templateLang: "en",
  headerFormat: "TEXT",
  headerText: "",
  headerExample: [],
  headerMediaHandle: null,
  bodyText: "",
  footerText: "",
  bodyExample: [],
  callToActions: [],
  quickReplies: [],
  catalogueFormat: 'catalogue',
  copyCodeButtonText: 'Copy code',
  selectedCodeDelivery: 'copy_code',
  useCustomValidity: true,
  validityPeriod: 600,
  securityRecommendation: true,
  expiresInMinutes: 10,
  isExpiryTime: false,
  isPaymentMethodSet: false,
  uploadedImage: null,
  uploadedVideo: null,
  uploadedDocument: null,
  imagePreview: null,
  videoPreview: null,
  documentPreview: null,
  languages: [
    { name: "Afrikaans", code: "af" },
    { name: "Albanian", code: "sq" },
    { name: "Arabic", code: "ar" },
    { name: "Arabic (EGY)", code: "ar_EG" },
    { name: "Arabic (UAE)", code: "ar_AE" },
    { name: "Arabic (LBN)", code: "ar_LB" },
    { name: "Arabic (MAR)", code: "ar_MA" },
    { name: "Arabic (QAT)", code: "ar_QA" },
    { name: "Azerbaijani", code: "az" },
    { name: "Belarusian", code: "be_BY" },
    { name: "Bengali", code: "bn" },
    { name: "Bengali (IND)", code: "bn_IN" },
    { name: "Bulgarian", code: "bg" },
    { name: "Catalan", code: "ca" },
    { name: "Chinese (CHN)", code: "zh_CN" },
    { name: "Chinese (HKG)", code: "zh_HK" },
    { name: "Chinese (TAI)", code: "zh_TW" },
    { name: "Croatian", code: "hr" },
    { name: "Czech", code: "cs" },
    { name: "Danish", code: "da" },
    { name: "Dari", code: "prs_AF" },
    { name: "Dutch", code: "nl" },
    { name: "Dutch (BEL)", code: "nl_BE" },
    { name: "English", code: "en" },
    { name: "English (UK)", code: "en_GB" },
    { name: "English (US)", code: "en_US" },
    { name: "English (UAE)", code: "en_AE" },
    { name: "English (AUS)", code: "en_AU" },
    { name: "English (CAN)", code: "en_CA" },
    { name: "English (GHA)", code: "en_GH" },
    { name: "English (IRL)", code: "en_IE" },
    { name: "English (IND)", code: "en_IN" },
    { name: "English (JAM)", code: "en_JM" },
    { name: "English (MYS)", code: "en_MY" },
    { name: "English (NZL)", code: "en_NZ" },
    { name: "English (QAT)", code: "en_QA" },
    { name: "English (SGP)", code: "en_SG" },
    { name: "English (UGA)", code: "en_UG" },
    { name: "English (ZAF)", code: "en_ZA" },
    { name: "Estonian", code: "et" },
    { name: "Filipino", code: "fil" },
    { name: "Finnish", code: "fi" },
    { name: "French", code: "fr" },
    { name: "French (BEL)", code: "fr_BE" },
    { name: "French (CAN)", code: "fr_CA" },
    { name: "French (CHE)", code: "fr_CH" },
    { name: "French (CIV)", code: "fr_CI" },
    { name: "French (MAR)", code: "fr_MA" },
    { name: "Georgian", code: "ka" },
    { name: "German", code: "de" },
    { name: "German (AUT)", code: "de_AT" },
    { name: "German (CHE)", code: "de_CH" },
    { name: "Greek", code: "el" },
    { name: "Gujarati", code: "gu" },
    { name: "Hausa", code: "ha" },
    { name: "Hebrew", code: "he" },
    { name: "Hindi", code: "hi" },
    { name: "Hungarian", code: "hu" },
    { name: "Indonesian", code: "id" },
    { name: "Irish", code: "ga" },
    { name: "Italian", code: "it" },
    { name: "Japanese", code: "ja" },
    { name: "Kannada", code: "kn" },
    { name: "Kazakh", code: "kk" },
    { name: "Kinyarwanda", code: "rw_RW" },
    { name: "Korean", code: "ko" },
    { name: "Kyrgyz (Kyrgyzstan)", code: "ky_KG" },
    { name: "Lao", code: "lo" },
    { name: "Latvian", code: "lv" },
    { name: "Lithuanian", code: "lt" },
    { name: "Macedonian", code: "mk" },
    { name: "Malay", code: "ms" },
    { name: "Malayalam", code: "ml" },
    { name: "Marathi", code: "mr" },
    { name: "Norwegian", code: "nb" },
    { name: "Pashto", code: "ps_AF" },
    { name: "Persian", code: "fa" },
    { name: "Polish", code: "pl" },
    { name: "Portuguese (BR)", code: "pt_BR" },
    { name: "Portuguese (POR)", code: "pt_PT" },
    { name: "Punjabi", code: "pa" },
    { name: "Romanian", code: "ro" },
    { name: "Russian", code: "ru" },
    { name: "Serbian", code: "sr" },
    { name: "Sinhala", code: "si_LK" },
    { name: "Slovak", code: "sk" },
    { name: "Slovenian", code: "sl" },
    { name: "Spanish", code: "es" },
    { name: "Spanish (ARG)", code: "es_AR" },
    { name: "Spanish (CHL)", code: "es_CL" },
    { name: "Spanish (COL)", code: "es_CO" },
    { name: "Spanish (CRI)", code: "es_CR" },
    { name: "Spanish (DOM)", code: "es_DO" },
    { name: "Spanish (ECU)", code: "es_EC" },
    { name: "Spanish (HND)", code: "es_HN" },
    { name: "Spanish (MEX)", code: "es_MX" },
    { name: "Spanish (PAN)", code: "es_PA" },
    { name: "Spanish (PER)", code: "es_PE" },
    { name: "Spanish (SPA)", code: "es_ES" },
    { name: "Spanish (URY)", code: "es_UY" },
    { name: "Swahili", code: "sw" },
    { name: "Swedish", code: "sv" },
    { name: "Tamil", code: "ta" },
    { name: "Telugu", code: "te" },
    { name: "Thai", code: "th" },
    { name: "Turkish", code: "tr" },
    { name: "Ukrainian", code: "uk" },
    { name: "Urdu", code: "ur" },
    { name: "Uzbek", code: "uz" },
    { name: "Vietnamese", code: "vi" },
    { name: "Zulu", code: "zu" }
  ],
}

export const getters={
  areCallToActionsValid: (state) => {
    if (
      state.selectedMessageType !== 'Default' ||
      state.callToActions.length === 0
    ) {
      return true;
    }

    return state.callToActions.every((btn) => {
      const hasText = (btn.text?.length || 0) > 0;

      if (btn.type === 'URL') {
        const hasUrl = (btn.url?.length || 0) > 0;
        const requiresExample = btn.url.includes('{{1}}');
        const hasExample = !requiresExample || (btn.example?.[0]?.length || 0) > 0;
        const isValidTemplateUrl = isValidUrlTemplate(btn.url);

        return hasText && hasUrl && hasExample && isValidTemplateUrl;
      }

      if (btn.type === 'PHONE_NUMBER') {
        return hasText && (btn.phone_number?.length || 0) > 0;
      }

      if (btn.type === 'COPY_CODE') {
        return hasText && (btn.example?.length || 0) > 0;
      }

      return hasText;
    });
  },
  isFormValid(state, getters) {
    const namePattern = /^[a-z0-9_]+$/;
    const isTemplateNameValid = namePattern.test(state.templateName) && state.templateName.length > 0;

    const bodyTextHasPlaceholder = state.bodyText?.includes('{{');

    const bodyExampleValid = bodyTextHasPlaceholder
      ? state.selectedFormat === 'POSITIONAL'
        ? Array.isArray(state.bodyExample) &&
          state.bodyExample.length > 0 &&
          state.bodyExample.every(val => typeof val === 'string' && val.trim().length > 0)
        : Array.isArray(state.bodyExample) &&
          state.bodyExample.every(item =>
            item.param_name &&
            /[a-zA-Z]/.test(item.param_name) && 
            item.example &&
            typeof item.example === 'string' &&
            item.example.trim().length > 0
          )
      : true;

    const headerTextHasPlaceholder = state.headerText?.includes('{{');
    const headerExampleValid = headerTextHasPlaceholder ? state.headerExample?.length > 0 : true;
    
    const isMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(state.headerFormat);
    const headerMediaValid = isMediaHeader ? !!state.headerMediaHandle : true;

    const quickRepliesValid = state.quickReplies.every(q => (q.text?.length || 0) > 0);
    const callToActionsValid = getters.areCallToActionsValid;

    if (state.currentCategory === 'MARKETING' && state.selectedMessageType === 'Default') {
      return (
        isTemplateNameValid &&
        state.bodyText.length > 0 &&
        bodyExampleValid &&
        headerExampleValid &&
        headerMediaValid &&
        quickRepliesValid &&
        callToActionsValid
      );
    } else if (state.currentCategory === 'UTILITY' && state.selectedMessageType === 'Default') {
      return (
        isTemplateNameValid &&
        state.bodyText.length > 0 &&
        bodyExampleValid &&
        headerExampleValid &&
        headerMediaValid &&
        quickRepliesValid &&
        callToActionsValid
      );
    } else if (state.currentCategory === 'AUTHENTICATION' && state.selectedMessageType === 'One-time passcode') {
      return (
        isTemplateNameValid &&
        state.copyCodeButtonText.length > 0
      );
    }
  }
}

export const mutations={
  setCurrentCategory(state, category) { state.currentCategory = category; },
  setSelectedFormat(state, format) { state.selectedFormat = format; },
  setIsPaymentMethodSet(state, value) { state.isPaymentMethodSet = value; },
  setCopyCodeButtonText(state, text) { state.copyCodeButtonText = text; },
  setUseCustomValidity(state, value) { state.useCustomValidity = value; },
  setValidityPeriod(state, value) { state.validityPeriod = value; },
  setSecurityRecommendation(state, value) { state.securityRecommendation = value; },
  setExpiresInMinutes(state, value) { state.expiresInMinutes = value; },
  setExpiryTime(state, value) { state.isExpiryTime = value; },
  setSelectedCodeDelivery(state, method) { state.selectedCodeDelivery = method; },
  setSelectedMessageType(state, type) { state.selectedMessageType = type; },
  setCatalogueFormat(state, format) { state.catalogueFormat = format; },
  setTemplateName(state, name) { state.templateName = name; },
  setTemplateLang(state, language) { state.templateLang = language; },
  setHeaderFormat(state, format) { state.headerFormat = format; },
  setHeaderText(state, text) { state.headerText = text; },
  setHeaderExample(state, example) { state.headerExample = example; },
  setHeaderMediaHandle(state, handle) { state.headerMediaHandle = handle; },
  setBodyText(state, text) { state.bodyText = text; },
  setBodyExample(state, example) { state.bodyExample = example; },
  setQuickReplies(state, buttons) { state.quickReplies = buttons; },
  setCallToActions(state, buttons) { state.callToActions = buttons; },
  setFooterText(state, text) { state.footerText = text; },
  setUploadedImage(state, file) { state.uploadedImage = file; },
  setUploadedVideo(state, file) { state.uploadedVideo = file; },
  setUploadedDocument(state, file) { state.uploadedDocument = file; },
  setImagePreview(state, val) { state.imagePreview = val; },
  setVideoPreview(state, val) { state.videoPreview = val; },
  setDocumentPreview(state, val) { state.documentPreview = val; },
  resetTemplateState(state) {
    state.selectedFormat = 'POSITIONAL';
    state.currentCategory = 'MARKETING';
    state.selectedMessageType = 'Default';
    state.templateName = '';
    state.templateLang = 'en';
    state.headerFormat = 'TEXT';
    state.headerText = '';
    state.headerExample = [];
    state.headerMediaHandle = null;
    state.bodyText = '';
    state.bodyExample = [];
    state.footerText = '';
    state.callToActions = [];
    state.quickReplies = [];
    state.catalogueFormat = 'catalogue';
    state.copyCodeButtonText = 'Copy code';
    state.selectedCodeDelivery = 'copy_code';
    state.useCustomValidity = true;
    state.validityPeriod = 600;
    state.securityRecommendation = true;
    state.expiresInMinutes = 10;
    state.isExpiryTime = false;
    state.isPaymentMethodSet = false;
    state.uploadedImage = null;
    state.uploadedVideo = null;
    state.uploadedDocument = null;
    state.imagePreview = null;
    state.videoPreview = null;
    state.documentPreview = null;
  }
}

export const actions = {
  async getAccessToken({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}`)
      );
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  // NOTE: checkPaymentRequirement calls a DIFFERENT internal URL — do NOT proxy
  async checkPaymentRequirement({ rootGetters, dispatch }, { inboxId }) {
    const url = new URL(`${window.location.origin}/api/v1/accounts/${rootGetters.getCurrentAccountId}/inboxes/${inboxId}/check_payment_requirement`);
    const token = await dispatch('getAccessToken');
    const rawResponse = await fetch(url.toString(), {
      headers: {
        "api_access_token": token
      }
    });

    if (!rawResponse.ok) {
      const errorData = await rawResponse.json();
      throw new Error(errorData.detail);
    }

    const response = await rawResponse.json();
    return response.requires_payment_method;
  },

  async fetchWhatsAppTemplates({ rootGetters, dispatch }, { inboxId }) {
    if (
      appConfig('VUE_APP_ENV') === 'development' &&
      Number(inboxId) === MOCK_WHATSAPP_INBOX_ID
    ) {
      return { templates: mockTemplates };
    }

    const accountId = rootGetters.getCurrentAccountId;
    try {
      await dispatch('syncWhatsAppTemplates', { inboxId });
      const { data: json } = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/whatsapp/templates/inboxes/${inboxId}`)
      );
      return json;
    } catch (err) {
      throw new Error("Error fetching WhatsApp template");
    }
  },

  async syncWhatsAppTemplates({ rootGetters }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data: json } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/whatsapp/templates/inboxes/${inboxId}/sync`)
      );
      return json;
    } catch (err) {
      throw new Error("Error syncing WhatsApp template");
    }
  },

  async createWhatsAppTemplates({ rootGetters, state }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    let components = [];

    if (
      state.selectedCodeDelivery === 'copy_code' &&
      state.selectedMessageType === 'One-time passcode'
    ) {
      const bodyComponent = { type: 'body' };
      if (state.securityRecommendation) {
        bodyComponent.add_security_recommendation = true;
      }
      components.push(bodyComponent);

      const footerComponent = { type: 'footer' };
      if (state.isExpiryTime && state.expiresInMinutes) {
        footerComponent.code_expiration_minutes = state.expiresInMinutes;
      }
      components.push(footerComponent);

      components.push({
        type: 'buttons',
        buttons: [
          {
            type: 'otp',
            otp_type: 'copy_code',
            ...(state.copyCodeButtonText ? { text: state.copyCodeButtonText } : {})
          }
        ]
      });
    } else {
      if (state.headerFormat === 'IMAGE' || state.headerFormat === 'VIDEO' || state.headerFormat === 'DOCUMENT') {
        const hasMatchingMedia = 
          (state.headerFormat === 'IMAGE' && (state.uploadedImage || state.imagePreview)) ||
          (state.headerFormat === 'VIDEO' && (state.uploadedVideo || state.videoPreview)) ||
          (state.headerFormat === 'DOCUMENT' && (state.uploadedDocument || state.documentPreview));
        
        if (state.headerMediaHandle && hasMatchingMedia) {
          components.push({
            type: 'HEADER',
            format: state.headerFormat,
            example: { header_handle: [state.headerMediaHandle] }
          });
        }
      } else if (state.headerFormat === 'LOCATION') {
        components.push({ type: 'HEADER', format: state.headerFormat });
      } else if (state.headerText) {
        const headerComponent = {
          type: 'HEADER',
          format: state.headerFormat,
          text: state.headerText
        };
        if (state.headerExample && state.headerExample.length) {
          headerComponent.example = { header_text: state.headerExample };
        }
        components.push(headerComponent);
      }

      if (state.bodyText) {
        const bodyComponent = { type: "BODY", text: state.bodyText };
        if (state.bodyExample && state.bodyExample.length) {
          if (state.selectedFormat === 'POSITIONAL') {
            bodyComponent.example = {
              body_text: state.bodyExample.length > 1 ? [state.bodyExample] : state.bodyExample,
            };
          } else {
            bodyComponent.example = { body_text_named_params: state.bodyExample };
          }
        }
        components.push(bodyComponent);
      }

      if (state.footerText) {
        components.push({ type: "FOOTER", text: state.footerText });
      }

      if (state.quickReplies.length > 0 || state.callToActions.length > 0) {
        const filteredCallToActions = state.callToActions.map(btn => {
          if (btn.type === 'URL') {
            if (btn.urlType === 'static') {
              const { urlType, example, ...rest } = btn;
              return rest;
            } else if (btn.urlType === 'dynamic') {
              const { urlType, ...rest } = btn;
              return rest;
            }
          }
          return btn;
        });

        components.push({
          type: "BUTTONS",
          buttons: [...state.quickReplies, ...filteredCallToActions]
        });
      }
      
      if (state.selectedMessageType === 'One-time passcode') {
        components.push({
          type: "BUTTONS",
          buttons: [{ "type": "otp", "otp_type": "copy_code", text: state.copyCodeButtonText }]
        });
      }
    }

    const payload = {
      name: state.templateName,
      language: state.templateLang,
      category: state.currentCategory,
      components,
    };

    if (
      state.selectedCodeDelivery === 'copy_code' &&
      state.selectedMessageType === 'One-time passcode'
    ) {
      payload.message_send_ttl_seconds = state.validityPeriod;
    }

    try {
      const { data: response } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/whatsapp/templates/inboxes/${inboxId}`),
        payload
      );
      return response;
    } catch (err) {
      const response = err.response?.data;
      throw {
        error_user_title: response?.detail?.error?.error_user_title || response?.detail?.error || "Template Creation Failed",
        error_user_msg: response?.detail?.error?.error_user_msg || response?.detail?.message || "Failed to create WhatsApp template"
      };
    }
  },

  async loadTemplateForView({ commit }, template) {
    commit('resetTemplateState');

    const components = template?.components || [];
    const find = type => components.find(c => c.type === type);
    const headerComponent = find('HEADER');
    const bodyComponent = find('BODY');
    const footerComponent = find('FOOTER');
    const buttonsComponent = find('BUTTONS');

    commit('setTemplateName', template?.name || '');
    commit('setTemplateLang', template?.language || 'en');
    commit('setCurrentCategory', template?.category || 'MARKETING');
    commit('setSelectedMessageType', template?.category === 'AUTHENTICATION' ? 'One-time passcode' : 'Default');

    if (headerComponent) {
      const format = headerComponent.format || 'TEXT';
      commit('setHeaderFormat', format);
      if (format === 'TEXT') {
        commit('setHeaderText', headerComponent.text || '');
        commit('setHeaderExample', headerComponent.example?.header_text?.[0] || '');
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
        const handle = headerComponent.example?.header_handle?.[0];
        const resolvedUrl = typeof handle === 'string' && handle.startsWith('http') ? handle : null;
        if (format === 'IMAGE') commit('setImagePreview', resolvedUrl);
        if (format === 'VIDEO') commit('setVideoPreview', resolvedUrl);
        if (format === 'DOCUMENT') commit('setDocumentPreview', resolvedUrl);
      }
    }

    commit('setBodyText', bodyComponent?.text || '');
    const bodyExample = bodyComponent?.example;
    if (bodyExample?.body_text_named_params) {
      commit('setSelectedFormat', 'NAMED');
      commit('setBodyExample', bodyExample.body_text_named_params);
    } else {
      commit('setSelectedFormat', 'POSITIONAL');
      commit('setBodyExample', bodyExample?.body_text?.[0] || []);
    }

    commit('setFooterText', footerComponent?.text || '');

    const buttons = buttonsComponent?.buttons || [];
    commit('setQuickReplies', buttons.filter(b => b.type === 'QUICK_REPLY'));
    commit('setCallToActions', buttons.filter(b => b.type !== 'QUICK_REPLY' && b.type !== 'OTP'));

    const otpButton = buttons.find(b => b.type === 'OTP');
    if (otpButton) {
      commit('setCopyCodeButtonText', otpButton.text || 'Copy code');
      commit('setSelectedCodeDelivery', otpButton.otp_type || 'copy_code');
    }
  },

  async deleteWhatsAppTemplate({ rootGetters }, { inboxId, templateId }) {
    if (
      appConfig('VUE_APP_ENV') === 'development' &&
      Number(inboxId) === MOCK_WHATSAPP_INBOX_ID
    ) {
      mockTemplates = mockTemplates.filter(t => t.id !== templateId);
      return true;
    }

    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/whatsapp/templates/inboxes/${inboxId}/template/${templateId}`)
      );
      return true;
    } catch (err) {
      throw new Error("Error deleting template");
    }
  },

  async uploadWhatsAppMedia({ rootGetters }, { inboxId, file }) {
    const accountId = rootGetters.getCurrentAccountId;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/whatsapp/media/inboxes/${inboxId}/upload`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Error uploading WhatsApp media');
    }
  },

  async getWhatsAppMediaUploadStatus({ rootGetters }, { inboxId, uploadSessionId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/whatsapp/media/inboxes/${inboxId}/upload/${uploadSessionId}/status`)
      );
      return data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Error getting WhatsApp media upload status');
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
